import { BrandLogo } from '@common/components/brand-logo';
import { LegalFooter } from '@common/components/legal-footer';
import { useEnvironmentShortcut } from '@common/hooks/use-environment-shortcut';
import { useLegalModalStore } from '@common/stores';
import { Icon } from '@konova/ui/icon';
import { Link } from '@konova/ui/link';
import { List, ListItem } from '@konova/ui/list';
import { Stack } from '@konova/ui/stack';
import { Typography } from '@konova/ui/typography';
import clsx from 'clsx';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { IOrganizationData } from '../../../../types';
import { useSupportUrl } from '../../hooks/use-support-url';
import classes from './footer.module.scss';

export type FooterProps = {
  lang: string;
  organisation: IOrganizationData;
};

export const Footer: FC<FooterProps> = ({ organisation, lang }) => {
  const envName = useEnvironmentShortcut();
  const { t } = useTranslation();
  const setShowPrivacyModal = useLegalModalStore(state => state.setShowPrivacyModal);
  const setPdfUrl = useLegalModalStore(state => state.setPdfUrl);
  const setShowImprintModal = useLegalModalStore(state => state.setShowImprintModal);
  const supportUrl = useSupportUrl();

  useEffect(() => {
    setPdfUrl(organisation.termsOfUsePdfUrl);
  }, [organisation.termsOfUsePdfUrl]);

  return (
    <div className={classes.rootWrapper}>
      <footer className={clsx('footer-component', classes.root)}>
        <div className={clsx('footer-container', classes.content)}>
          <div className={clsx('footer-logo', classes.logoWrapper)}>
            <BrandLogo
              src={organisation.logo_path ? organisation.logoUrl : undefined}
              alt={organisation.organization}
              className={clsx('footer-component__organisation-logo', classes.logo)}
            />
          </div>

          <Stack direction="column" spacing={3} className={classes.addressInfoWrapper}>
            <Stack direction="column" spacing={2}>
              <Typography as="h3" variant="h3" bold>
                {t('common.contact')}
              </Typography>

              <Stack
                className="footer-component__address"
                direction="column"
                spacing={0}
              >
                <Typography as="p">{organisation.organization}</Typography>

                <Typography as="p">{organisation.organization_address}</Typography>

                <Typography as="p">{organisation.organization_address2}</Typography>

                <Typography as="p">
                  {organisation.organization_zip}
                  {' '}
                  {organisation.organization_city}
                </Typography>
              </Stack>

              <Stack
                className="footer-component__contact-links"
                direction="column"
                spacing={0}
              >
                {organisation.organization_phone && (
                  <Typography as="p">
                    <Link
                      color="primary.main"
                      as="a"
                      href={`tel:${organisation.organization_phone}`}
                    >
                      {organisation.organization_phone}
                    </Link>
                  </Typography>
                )}

                <Typography as="p">
                  <Link
                    color="primary.main"
                    as="a"
                    href={`mailto:${organisation.organization_mail}`}
                  >
                    {organisation.organization_mail}
                  </Link>
                </Typography>

                {organisation.organization_website && (
                  <Typography as="p">
                    <Link
                      color="primary.main"
                      as="a"
                      href={organisation.organization_website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {organisation.organization_website?.replace(/^https?:\/\//, '')}
                    </Link>
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          <Stack
            direction="column"
            spacing={2}
            className={clsx('footer-links footer-component__links', classes.linksWrapper)}
          >
            <Typography as="h3" variant="h3" bold>
              {t('common.learn-more')}
            </Typography>

            <List noPadding>
              <ListItem prefix={<Icon color="primary" icon={['fal', 'arrow-right']} />}>
                <Link
                  color="primary"
                  as={RouterLink}
                  to={supportUrl}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {t('common.support')}
                </Link>
              </ListItem>

              {organisation.infoLink && (
                <ListItem prefix={<Icon color="primary" icon={['fal', 'arrow-right']} />}>
                  <Link as="a" href={organisation.infoLink?.url}>
                    {t('common.info')}
                  </Link>
                </ListItem>
              )}

              <ListItem prefix={<Icon color="primary" icon={['fal', 'arrow-right']} />}>
                <Link
                  as="button"
                  data-testid="open-privacy-modal"
                  onClick={() => setShowPrivacyModal(true)}
                >
                  {t('common.privacy')}
                </Link>
              </ListItem>

              <ListItem prefix={<Icon color="primary" icon={['fal', 'arrow-right']} />}>
                <Link
                  as={RouterLink}
                  to={`/${lang}/accessibility`}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {t('common.accessibility')}
                </Link>
              </ListItem>
            </List>
          </Stack>

          <LegalFooter
            onShowImprintClicked={() => setShowImprintModal(true)}
            envName={envName}
          />
        </div>
      </footer>
    </div>
  );
};